import { useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import {
  WhmStack,
  WhmFormControl,
  WhmButton,
  useWhmSnackbar,
} from "@securitize/reactjs-whm";
import FormBlock from "./FormBlock";
import { W9FormType } from "./FormType";
import {
  addressSchema,
  fullNameSchema,
  nameConfirmationSchema,
  RegionSchema,
  taxPayerSchema,
} from "./FormValidationSchemas";
import W9TaxInformationInputs from "./Inputs/W9TaxInformationInputs";
import ExemptionsInputs from "./Inputs/ExemptionsInputs";
import CertificationInputs from "./Inputs/CertificationInputs";
import PersonalInformationInputs from "./Inputs/PersonalInformationInputs";
import AddressInputs from "./Inputs/AddressInputs";
import { InvestorInfo } from "../../../types/investorInformation.interface";
import { KycStatus, UserInfo } from "../../../types/UserInfo";
import FormConfirmationModal from "./FormConfirmationModal";
import { markTaxFormSubmitted } from "../../Dashboard/components/TaxFormSubmittedModal";
import { postW9TaxForm } from "../../../data/rest/taxForms";
import {
  InvestorType,
  TaxFormType,
  W9TaxIdType,
} from "../../../types/documents.interface";
import useTranslations from "../../../hooks/useTranslations";
import { translationKeys } from "../../../contexts/I18nContext";
import useMixPanel, { TrackingType } from "../../../hooks/useMixPanel";
import { getInvestorTaxInfo } from "../../../helpers/investor.utils";
import W9FormSkeleton from "./W9Form.skeleton";
import { useMFEContext } from "../../../contexts/MFEContext";
import useRefreshForm from "../../../hooks/useRefreshForm";

const validationSchema = yup.object({
  ...addressSchema({
    address1: "address1",
    address2: "address2",
    locality: "city",
    postalCode: "postalCode",
  }),
  ...RegionSchema({
    region: "region",
  }),
  ...fullNameSchema({
    name: "formInvestorName",
    businessName: "businessName",
  }),
  ...taxPayerSchema({
    classification: "formClassification",
    identifier: "taxPayerIdType",
    exemptPayeeCodes: "exemptPayeeCodes",
    exemptFATCAReportingCodes: "exemptFATCAReportingCodes",
    taxPayerIdNumber: "taxPayerIdNumber",
  }),
  ...nameConfirmationSchema({
    nameConfirmation: "nameConfirmation",
    agree: "agree",
  }),
});

type Props = {
  isLoading?: boolean;
  userInfo: UserInfo;
  investorInfo: InvestorInfo;
  disableInputs?: boolean;
};

const W9TaxForm = ({
  isLoading,
  userInfo,
  investorInfo,
  disableInputs,
}: Props) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<W9FormType | undefined>();
  const snackbar = useWhmSnackbar();
  const { onUpdate } = useMFEContext();
  const track = useMixPanel();
  const { getTranslation: t } = useTranslations();
  const { refresh } = useRefreshForm();

  const onSubmit = async (values: W9FormType) => {
    setIsSubmitted(true);
    track(TrackingType.TA_PAYOUTS_W9_SUBMIT);
    await postW9TaxForm(
      values,
      ![KycStatus.NOT_VERIFIED, KycStatus.REJECTED].includes(
        userInfo.verificationStatus,
      ),
    );
    markTaxFormSubmitted(TaxFormType.W9);
    snackbar.show(
      t(translationKeys.FORM_FEEDBACK_SUCCESS_DOCUMENT, {
        document: TaxFormType.W9,
      }),
      {
        severity: "success",
      },
    );
    onUpdate?.();
    refresh();
  };

  if (isLoading) return <W9FormSkeleton />;

  const isCheckedAndSigned = (values: W9FormType) =>
    Boolean(values.agree) && Boolean(values.nameConfirmation);
  const tax = getInvestorTaxInfo(investorInfo, "US");

  const getResponsibleUserName = () => {
    if (userInfo.investorType === InvestorType.ENTITY) {
      // Find the individual investor (responsible user) from userInvestors array
      const responsibleUser = userInfo.userInvestors?.find(
        investor => investor.details.investorType === 'individual'
      );
      
      if (responsibleUser?.details.firstName && responsibleUser?.details.lastName) {
        return `${responsibleUser.details.firstName} ${responsibleUser.details.lastName}`;
      }
      
      // Fallback to displayName if firstName/lastName not available
      if (responsibleUser?.details.displayName) {
        return responsibleUser.details.displayName;
      }
      
      // Final fallback to entity name if no responsible user found
      return userInfo.entityName;
    }
    
    return userInfo.fullName || investorInfo.fullName;
  };

  const fullName = getResponsibleUserName();

  const initialValues = {
    nameConfirmation: fullName,
    formInvestorName: fullName,
    businessName:
      userInfo.investorType === InvestorType.INDIVIDUAL
        ? ""
        : userInfo.entityName,
    address1:
      investorInfo?.address?.street || investorInfo?.address?.houseNumber
        ? `${investorInfo?.address?.street} ${investorInfo?.address?.houseNumber}`
        : "",
    address2: investorInfo?.address?.entrance || "",
    city: investorInfo?.address?.city || "",
    country: investorInfo?.address?.countryCode || "",
    region: investorInfo?.address?.state || "",
    postalCode: investorInfo?.address?.zip || "",
    formClassification:
      userInfo.investorType === InvestorType.INDIVIDUAL
        ? InvestorType.INDIVIDUAL
        : "",
    taxPayerIdType: W9TaxIdType.SSN,
    taxPayerIdNumber: tax.taxPayerIdNumber,
    taxCountry: tax.taxCountry,
    exemptPayeeCodes: "",
    exemptFATCAReportingCodes: "",
    formDate: moment().format("YYYY-MM-DD").toString(),
    agree: false,
  };

  const initialTouched: Record<string, boolean> = Object.keys(
    initialValues,
  ).reduce<Record<string, boolean>>((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        initialTouched={initialTouched}
        onSubmit={(values) => {
          setShowConfirmationModal(true);
          setFormData(values as W9FormType);
        }}
      >
        {({ isValid, values, submitForm }) => (
          <WhmFormControl fullWidth>
            <WhmStack spacing={6}>
              <FormBlock
                title={t(translationKeys.FORM_SECTION_PERSONAL_TITLE)}
                description={t(translationKeys.FORM_SECTION_PERSONAL_SUBTITLE)}
              >
                <PersonalInformationInputs disabled={disableInputs} />
              </FormBlock>
              <FormBlock
                title={t(translationKeys.FORM_W9_SECTION_ADDRESS_TITLE)}
                description={t(
                  translationKeys.FORM_W9_SECTION_ADDRESS_SUBTITLE,
                )}
              >
                <AddressInputs disabled={disableInputs} />
              </FormBlock>
              <FormBlock
                title={t(translationKeys.FORM_SECTION_TAX_INFORMATION_TITLE)}
                description={t(
                  translationKeys.FORM_SECTION_TAX_INFORMATION_SUBTITLE,
                )}
              >
                <W9TaxInformationInputs
                  investorType={userInfo.investorType}
                  disabled={disableInputs}
                />
              </FormBlock>
              <FormBlock
                title={t(translationKeys.FORM_SECTION_EXEMPTIONS_TITLE)}
                description={t(
                  translationKeys.FORM_SECTION_EXEMPTIONS_SUBTITLE,
                )}
              >
                <ExemptionsInputs />
              </FormBlock>
              <FormBlock
                title={t(translationKeys.FORM_SECTION_CERTIFICATION_TITLE)}
                description={t(
                  translationKeys.FORM_SECTION_CERTIFICATION_SUBTITLE,
                )}
              >
                <CertificationInputs formType={TaxFormType.W9} />
              </FormBlock>
              <WhmButton
                variant="contained"
                color="primary"
                sx={{ width: "fit-content" }}
                disabled={
                  !(isValid && isCheckedAndSigned(values as W9FormType)) ||
                  isSubmitted
                }
                onClick={submitForm}
              >
                {t(translationKeys.FORM_BUTTONS_SUBMIT)}
              </WhmButton>
            </WhmStack>
          </WhmFormControl>
        )}
      </Formik>
      {Boolean(formData) && showConfirmationModal && (
        <FormConfirmationModal
          open={showConfirmationModal}
          onConfirm={() => onSubmit(formData as W9FormType)}
          onClose={() => setShowConfirmationModal(false)}
          onCancel={() => setShowConfirmationModal(false)}
          onError={() => {
            setShowConfirmationModal(false);
            setIsSubmitted(false);
            snackbar.show(t(translationKeys.FORM_FEEDBACK_ERROR), {
              severity: "error",
            });
          }}
        />
      )}
    </div>
  );
};

export default W9TaxForm;
